/**
 * Preserve arbitrary landing-URL query params across the whole booking flow and
 * hand them off to Shopify Checkout.
 *
 * Why this exists
 * ---------------
 * Shopify Campaigns attributes sales using URL query params (utm_campaign,
 * utm_source, …). The booking flow rewrites the URL via react-router as the user
 * advances (see `useUrlParams`), and that rewrite only re-emits a known
 * allow-list of booking params — every other param is dropped after the very
 * first step. That broke attribution: the tracking params never reached
 * Checkout.
 *
 * Rather than special-casing utm_* we snapshot *all* non-booking params from the
 * initial landing URL and persist them in `sessionStorage` (which survives
 * in-app navigation and full-page reloads within the same tab, unlike the URL
 * that the router keeps rewriting). At checkout hand-off we merge them into the
 * destination URL. This keeps the intricate navigation logic untouched, so
 * existing booking behaviour is unaffected, while supporting any arbitrary
 * query param now and in the future.
 */

import { BOOKING_QUERY_KEYS } from "@/shared/utils/bookingUrl";

const STORAGE_KEY = "hk_passthrough_query_params";

/**
 * Booking-internal params that drive the UI/routing, plus the login-error
 * signals. These are app state, not tracking params, so they are excluded from
 * the snapshot and never forwarded to Checkout. Everything else on the landing
 * URL is preserved.
 *
 * The booking keys are derived from `BOOKING_QUERY_KEYS` (which covers both the
 * short wire keys and the superseded long names) rather than repeated here: a
 * key missing from this set would be mistaken for a campaign param and leak
 * booking state into Checkout.
 */
const INTERNAL_PARAM_KEYS = new Set<string>([...BOOKING_QUERY_KEYS, "login_error", "auth_error", "customer_login_error"]);

/** Guards against re-snapshotting once the flow has already started navigating. */
let hasCaptured = false;

/**
 * Snapshot the passthrough (non-booking) query params from the initial landing
 * URL. Call this as early as possible (before the router rewrites the URL).
 *
 * Idempotent within a page load. Safe to call when `sessionStorage` is
 * unavailable (private mode / SSR): it simply no-ops. If the current URL carries
 * no passthrough params (e.g. a later full-page reload landed on a rewritten
 * step URL) the previously captured snapshot is left intact.
 */
export const captureInitialQueryParams = (search: string = typeof window !== "undefined" ? window.location.search : ""): void => {
  if (typeof window === "undefined" || hasCaptured) return;
  hasCaptured = true;

  try {
    const incoming = new URLSearchParams(search);
    const passthrough: Record<string, string> = {};
    incoming.forEach((value, key) => {
      if (!INTERNAL_PARAM_KEYS.has(key)) {
        passthrough[key] = value;
      }
    });

    // Nothing new to capture — preserve any snapshot from the true landing hit.
    if (Object.keys(passthrough).length === 0) return;

    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(passthrough));
  } catch {
    // sessionStorage can throw (Safari private mode, quota). Attribution is
    // best-effort, so never let it break the booking flow.
  }
};

/** Read the persisted passthrough params. Returns `{}` when none/unavailable. */
export const getPersistedQueryParams = (): Record<string, string> => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as Record<string, string>) : {};
  } catch {
    return {};
  }
};

/**
 * Merge the persisted passthrough params into `targetUrl`, returning the new URL.
 *
 * Handles both absolute URLs (headless `checkoutUrl`) and relative paths
 * (`/checkout`), preserving the original form. Params already present on
 * `targetUrl` win and are never overwritten. Returns `targetUrl` unchanged when
 * there is nothing to add or the URL cannot be parsed.
 */
export const appendPersistedQueryParams = (targetUrl: string): string => {
  const params = getPersistedQueryParams();
  const keys = Object.keys(params);
  if (keys.length === 0) return targetUrl;

  try {
    const base = typeof window !== "undefined" ? window.location.origin : "https://placeholder.invalid";
    const url = new URL(targetUrl, base);

    keys.forEach((key) => {
      if (!url.searchParams.has(key)) {
        url.searchParams.set(key, params[key]);
      }
    });

    const isAbsolute = /^[a-z][a-z0-9+.-]*:\/\//i.test(targetUrl);
    return isAbsolute ? url.toString() : url.pathname + url.search + url.hash;
  } catch {
    return targetUrl;
  }
};
